import React, { useState, useEffect, useRef } from "react";
import { Button, Card, CardBody, CardHeader, Container, Row, Col, Modal, ModalBody, ModalFooter, ModalHeader, Form, FormGroup, Input, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Eye, MoreVertical, Trash, SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Flatpickr from "react-flatpickr";

const InvoiceList = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [taxes, setTaxes] = useState([]);
    // const [selectedTax, setSelectedTax] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [invoiceToDelete, setInvoiceToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        invoice_number: "",
        client_id: "",
        invoice_date: "",
        status: "draft",
        tax_id: null,
        subtotal: 0,
        tax_amount: 0,
        discount_percentage: "",
        discount_amount: "",
        total_amount: 0,
        invoice_items: []
    });

    const [currentItem, setCurrentItem] = useState({
        description: "",
        quantity: "",
        unit_price: "",
        amount: ""
    });

    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);
    const toast = useRef(null);

    const toggle = () => {
        if (!modal && !formData.id) {  // Only reset if not editing
            resetForm();
        }
        setModal(!modal);
    };

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const breadcrumbItems = [
        { title: "Accounts", link: "/" },
        { title: "Invoices", link: "/admin/accounts/invoice" },
    ];

    const viewInvoice = (invoice) => {
        navigate(`/admin/accounts/invoice/${invoice.client_id}`);
    };

    const fetchClients = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/clients/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            setClients(response.data.data || []);
        } catch (error) {
            console.error("Error fetching clients:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch clients',
                life: 3000
            });
        }
    };

    const fetchTaxes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/taxes/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            setTaxes(response.data.data || []);
        } catch (error) {
            console.error("Error fetching taxes:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch taxes',
                life: 3000
            });
        }
    };

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/invoice/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            const invoiceData = response.data.data || [];
            setInvoices(invoiceData);
            setFilteredInvoices(invoiceData);
            setTotalRecords(invoiceData.length);
        } catch (error) {
            console.error("Error fetching invoices:", error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch invoices',
                life: 3000
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClients();
        fetchTaxes();
        fetchInvoices();
    }, []);

    useEffect(() => {
        let filtered = invoices;

        if (filterStatus) {
            filtered = filtered.filter(invoice => invoice.status === filterStatus);
        }

        if (filterClient) {
            filtered = filtered.filter(invoice => invoice.client_id === filterClient);
        }

        if (searchTerm) {
            filtered = filtered.filter(invoice =>
                invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredInvoices(filtered);
        setTotalRecords(filtered.length);
    }, [filterStatus, filterClient, searchTerm, invoices]);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    useEffect(() => {
        calculateTotals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.invoice_items, formData.tax_id, formData.discount_amount, formData.discount_percentage, taxes]);

    const calculateTotals = () => {
        // Calculate subtotal from invoice items
        const newSubtotal = formData.invoice_items.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
        }, 0);

        // Calculate tax if tax is selected
        let newTaxAmount = 0;
        const tax = taxes.find(t => t.id === formData.tax_id);
        if (tax) {
            newTaxAmount = (newSubtotal * parseFloat(tax.gst)) / 100;
        }

        // Calculate discount
        let discount = 0;
        let discountType = null;

        if (formData.discount_amount && parseFloat(formData.discount_amount) > 0) {
            discount = parseFloat(formData.discount_amount);
            discountType = 'fixed';
        } else if (formData.discount_percentage && parseFloat(formData.discount_percentage) > 0 && newSubtotal > 0) {
            discount = (newSubtotal * parseFloat(formData.discount_percentage)) / 100;
            discountType = 'percentage';
        }

        // Calculate total
        const newTotal = newSubtotal + newTaxAmount - discount;

        setFormData(prev => ({
            ...prev,
            subtotal: newSubtotal,
            tax_amount: newTaxAmount,
            discount_type: discountType,
            total_amount: newTotal
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.invoice_items.length === 0) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'At least one invoice item is required',
                life: 3000
            });
            return;
        }

        // Format the date for MySQL (YYYY-MM-DD)
        const formatDateForMySQL = (date) => {
            if (!date) return null;
            const d = new Date(date);
            return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
        };

        // Prepare items ensuring all values are properly formatted
        const preparedItems = formData.invoice_items.map(item => ({
            description: item.description,
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price) || 0,
            amount: parseFloat(item.amount) || 0
        }));

        // Calculate the subtotal directly from items to ensure it's accurate
        const calculatedSubtotal = preparedItems.reduce((sum, item) => sum + item.amount, 0);

        // Explicitly format tax and discount values to ensure they're sent correctly
        const taxId = formData.tax_id || null;
        const discountPercentage = formData.discount_percentage ? parseFloat(formData.discount_percentage) : null;
        const discountAmount = formData.discount_amount ? parseFloat(formData.discount_amount) : null;

        // Calculate tax amount based on the selected tax
        let taxAmount = 0;
        if (taxId) {
            const tax = taxes.find(t => t.id === taxId);
            if (tax) {
                taxAmount = (calculatedSubtotal * parseFloat(tax.gst)) / 100;
            }
        }

        // Calculate final amount with discount
        let finalDiscount = 0;
        if (discountAmount) {
            finalDiscount = discountAmount;
        } else if (discountPercentage) {
            finalDiscount = (calculatedSubtotal * discountPercentage) / 100;
        }

        // Calculate total with tax and discount
        const totalAmount = calculatedSubtotal + taxAmount - finalDiscount;

        // Add discount_type field based on which discount input is used
        const discountType = discountPercentage ? 'percentage' : (discountAmount ? 'fixed' : null);

        const data = {
            ...formData,
            invoice_date: formatDateForMySQL(formData.invoice_date),
            invoice_items: preparedItems,
            subtotal: calculatedSubtotal.toFixed(2),  // Ensure subtotal is formatted as string with 2 decimal places
            tax_id: taxId,
            tax_amount: taxAmount.toFixed(2),         // Ensure tax_amount is formatted as string with 2 decimal places
            discount_percentage: discountPercentage,
            discount_amount: finalDiscount.toFixed(2), // Ensure discount_amount is formatted as string with 2 decimal places
            discount_type: discountType,               // Add discount_type to payload
            total_amount: totalAmount.toFixed(2),      // Ensure total_amount is formatted as string with 2 decimal places
            is_tax_applied: taxId ? 1 : 0              // Add is_tax_applied flag
        };

        console.log("Sending payload:", data); // For debugging

        setLoading(true);
        try {
            let response;
            if (formData.id) {
                response = await axios.post(`${API_BASE_URL}/invoice/update/${formData.id}`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Invoice updated successfully',
                    life: 3000
                });
            } else {
                response = await axios.post(`${API_BASE_URL}/invoice/add`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({
                    severity: 'success',
                    summary: 'Added',
                    detail: 'Invoice added successfully',
                    life: 3000
                });
            }

            console.log("Response:", response.data); // For debugging
            fetchInvoices();
            toggle();
        } catch (error) {
            console.error("Error details:", error.response?.data);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to save invoice',
                life: 3000
            });
        }
        setLoading(false);
    };

    const editInvoice = (invoice) => {
        // Parse the invoice date correctly
        let invoiceDate = '';
        try {
            if (invoice.invoice_date) {
                // Create date in local timezone (avoids timezone conversion issues)
                const dateParts = invoice.invoice_date.split('T')[0].split('-');
                invoiceDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
            } else {
                invoiceDate = new Date();
            }
        } catch (e) {
            invoiceDate = new Date();
        }

        // Safely parse invoice items
        const preparedItems = (invoice.invoice_items || []).map(item => ({
            id: item.id || null,
            description: item.description || "",
            quantity: item.quantity ? item.quantity.toString() : "1",
            unit_price: item.unit_price ? item.unit_price.toString() : "0",
            amount: item.amount ? item.amount.toString() : "0"
        }));

        // Handle discount values based on discount_type from server
        let discountPercentage = "";
        let discountAmount = "";

        if (invoice.discount_type === 'percentage' && invoice.discount_percentage) {
            discountPercentage = invoice.discount_percentage.toString();
            // Calculate discount amount based on percentage for display
            discountAmount = ((parseFloat(invoice.subtotal || 0) * parseFloat(discountPercentage)) / 100).toFixed(2);
        } else if (invoice.discount_amount && parseFloat(invoice.discount_amount) > 0) {
            discountAmount = parseFloat(invoice.discount_amount).toString();
            // Calculate percentage based on amount for display if subtotal exists
            if (parseFloat(invoice.subtotal || 0) > 0) {
                discountPercentage = ((parseFloat(discountAmount) / parseFloat(invoice.subtotal)) * 100).toFixed(2);
            }
        }

        // Calculate correct subtotal from items if server doesn't provide it
        const calculatedSubtotal = preparedItems.reduce((sum, item) =>
            sum + (parseFloat(item.amount) || 0), 0);

        // Prepare the form data
        const updatedFormData = {
            id: invoice.id,
            invoice_number: invoice.invoice_number || "",
            client_id: invoice.client_id ? invoice.client_id.toString() : "",
            invoice_date: invoiceDate,
            status: invoice.status || "draft",
            tax_id: invoice.tax_id ? invoice.tax_id.toString() : null,
            subtotal: parseFloat(invoice.subtotal || calculatedSubtotal),
            tax_amount: parseFloat(invoice.tax_amount || 0),
            discount_percentage: discountPercentage,
            discount_amount: discountAmount,
            total_amount: parseFloat(invoice.total_amount || 0),
            invoice_items: preparedItems,
            discount_type: invoice.discount_type || null,
            is_tax_applied: invoice.is_tax_applied || (invoice.tax_id ? 1 : 0)
        };

        console.log("Edit form data:", updatedFormData); // For debugging
        setFormData(updatedFormData);
        setModal(true);
    };


    const addItem = () => {
        if (!currentItem.description || !currentItem.quantity || !currentItem.unit_price) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please fill all item fields',
                life: 3000
            });
            return;
        }

        const amount = parseFloat(currentItem.quantity) * parseFloat(currentItem.unit_price);
        const newItem = {
            description: currentItem.description,
            quantity: currentItem.quantity,
            unit_price: currentItem.unit_price,
            amount: amount.toFixed(2)
        };

        setFormData(prev => ({
            ...prev,
            invoice_items: [...prev.invoice_items, newItem]
        }));

        // Reset current item
        setCurrentItem({
            description: "",
            quantity: "",
            unit_price: "",
            amount: ""
        });
    };

    const removeItem = (index) => {
        const updatedItems = [...formData.invoice_items];
        updatedItems.splice(index, 1);
        setFormData(prev => ({
            ...prev,
            invoice_items: updatedItems
        }));
    };

    const handleDelete = async () => {
        if (!invoiceToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/invoice/delete/${invoiceToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            toast.current?.show({
                severity: "success",
                summary: "Deleted",
                detail: "Invoice deleted successfully",
                life: 3000
            });
            fetchInvoices();
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to delete invoice",
                life: 3000
            });
        }
        setInvoiceToDelete(null);
        toggleDeleteModal();
    };

    const resetForm = () => {
        setFormData({
            invoice_number: "",
            client_id: "",
            invoice_date: "",
            status: "draft",
            tax_id: null,
            subtotal: 0,
            tax_amount: 0,
            discount_percentage: "",
            discount_amount: "",
            total_amount: 0,
            invoice_items: []
        });
        setCurrentItem({
            description: "",
            quantity: "",
            unit_price: "",
            amount: ""
        });
        setDropdownOpen(null);
    };


    const clientNameBodyTemplate = (rowData) => {
        return rowData.clients?.name || "N/A";
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Invoices" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Invoice List</h4>
                            <div>
                                <Button color="success" onClick={() => setShowFilters(!showFilters)} className="me-2">
                                    <SlidersHorizontal size={13} /> Filter
                                </Button>
                                <Button onClick={toggle} color="primary">
                                    <i className="mdi mdi-plus mr-1"></i>Add Invoice
                                </Button>
                            </div>
                        </CardHeader>

                        {/* Filter Section */}
                        {showFilters && (
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Search Invoice Number</Label>
                                            <Input
                                                type="text"
                                                placeholder="Search by invoice number"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Status</Label>
                                            <Input
                                                type="select"
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="draft">Draft</option>
                                                <option value="sent">Sent</option>
                                                <option value="paid">Paid</option>
                                                <option value="overdue">Overdue</option>
                                                <option value="cancelled">Cancelled</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Client</Label>
                                            <Input
                                                type="select"
                                                value={filterClient}
                                                onChange={(e) => setFilterClient(e.target.value)}
                                            >
                                                <option value="">All Clients</option>
                                                {clients.map(client => (
                                                    <option key={client.id} value={client.id}>
                                                        {client.name} ({client.company_name || 'No company'})
                                                    </option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        )}

                        <CardBody>
                            <DataTable
                                value={filteredInvoices.slice(first, first + rows)}
                                loading={loading}
                                stripedRows
                            >
                                <Column
                                    header="SI No"
                                    body={(rowData, { rowIndex }) => rowIndex + 1 + first}
                                />
                                <Column header="Invoice Number" field="invoice_number" sortable />
                                <Column header="Client" body={clientNameBodyTemplate} sortable />
                                <Column header="Date" field="invoice_date" sortable />
                                {/* <Column 
                                    header="Total Amount" 
                                    body={(rowData) => `$${parseFloat(rowData.total_amount).toFixed(2)}`} 
                                    sortable 
                                /> */}
                                {/* <Column header="Status" body={statusBodyTemplate} sortable /> */}
                                <Column
                                    header="Actions"
                                    body={(rowData) => (
                                        <Dropdown
                                            isOpen={dropdownOpen === rowData.id}
                                            toggle={() => toggleDropdown(rowData.id)}
                                        >
                                            <DropdownToggle tag="button" className="btn btn-light">
                                                <MoreVertical size={20} />
                                            </DropdownToggle>
                                            <DropdownMenu>
                                                <DropdownItem
                                                    size="sm"
                                                    className="me-2 text-primary"
                                                    onClick={() => viewInvoice(rowData)}
                                                >
                                                    <Eye size={16} className="me-2" />
                                                    View
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => editInvoice(rowData)}
                                                >
                                                    <Edit size={16} className="me-2" />
                                                    Edit
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={() => {
                                                        setInvoiceToDelete(rowData);
                                                        toggleDeleteModal();
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <Trash size={16} className="me-2" />
                                                    Delete
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                />
                            </DataTable>

                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPageChange={onPageChange}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>

            {/* Add/Edit Invoice Modal */}
            <Modal isOpen={modal} centered toggle={toggle} size="lg">
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={() => { toggle(); resetForm(); }}>
                        {formData.id ? 'Edit Invoice' : 'Add Invoice'}
                    </ModalHeader>
                    <ModalBody>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Invoice Number</Label>
                                    <Input
                                        type="text"
                                        value={formData.invoice_number}
                                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Client</Label>
                                    <Input
                                        type="select"
                                        value={formData.client_id || ""}
                                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Client</option>
                                        {clients.map(client => (
                                            <option key={client.id} value={client.id}>
                                                {client.name} ({client.company_name || 'No company'})
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Invoice Date</Label>
                                    <Flatpickr
                                        className="form-control"
                                        value={formData.invoice_date}
                                        onChange={(date) => {
                                            // Create date in local timezone without time component
                                            const selectedDate = date[0] ?
                                                new Date(date[0].getFullYear(), date[0].getMonth(), date[0].getDate()) :
                                                new Date();

                                            setFormData({
                                                ...formData,
                                                invoice_date: selectedDate
                                            });
                                        }}
                                        options={{
                                            dateFormat: 'Y-m-d',
                                            defaultDate: formData.invoice_date,
                                            allowInput: true
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Status</Label>
                                    <Input
                                        type="select"
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="draft" selected={formData.status === 'draft'}>Draft</option>
                                        <option value="sent" selected={formData.status === 'sent'}>Sent</option>
                                        <option value="paid" selected={formData.status === 'paid'}>Paid</option>
                                        <option value="overdue" selected={formData.status === 'overdue'}>Overdue</option>
                                        <option value="cancelled" selected={formData.status === 'cancelled'}>Cancelled</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>



                        <h5 className="mt-4">Invoice Items</h5>
                        <Row>
                            <Col md={5}>
                                <FormGroup>
                                    <Label>Description</Label>
                                    <Input
                                        type="text"
                                        value={currentItem.description}
                                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Quantity</Label>
                                    <Input
                                        type="number"
                                        value={currentItem.quantity}
                                        onChange={(e) => {
                                            const quantity = e.target.value;
                                            const unitPrice = currentItem.unit_price || 0;
                                            setCurrentItem({
                                                ...currentItem,
                                                quantity: quantity,
                                                amount: (quantity * unitPrice).toFixed(2)
                                            });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Unit Price</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={currentItem.unit_price}
                                        onChange={(e) => {
                                            const unitPrice = e.target.value;
                                            const quantity = currentItem.quantity || 0;
                                            setCurrentItem({
                                                ...currentItem,
                                                unit_price: unitPrice,
                                                amount: (quantity * unitPrice).toFixed(2)
                                            });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Amount</Label>
                                    <Input
                                        type="text"
                                        value={currentItem.amount}
                                        readOnly
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={1} className="d-flex align-items-end">
                                <Button color="primary" onClick={addItem} className="mb-3">
                                    Add
                                </Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Tax</Label>
                                    <Input
                                        type="select"
                                        value={formData.tax_id || ''}
                                        onChange={(e) => {
                                            const taxId = e.target.value;
                                            setFormData({
                                                ...formData,
                                                tax_id: taxId || null
                                            });
                                        }}
                                    >
                                        <option value="">No Tax</option>
                                        {taxes.map(tax => (
                                            <option key={tax.id} value={tax.id} selected={tax.id === formData.tax_id}>
                                                {tax.name} - {tax.gst}%
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Subtotal</Label>
                                    <Input
                                        type="text"
                                        value={formData.subtotal.toFixed(2)}
                                        readOnly
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Tax Amount</Label>
                                    <Input
                                        type="text"
                                        value={formData.tax_amount.toFixed(2)}
                                        readOnly
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>

                            <Col md={4}>
                                <FormGroup>
                                    <Label>Discount Percentage</Label>
                                    <Input
                                        type="number"
                                        value={formData.discount_percentage}
                                        onChange={(e) => {
                                            setFormData({
                                                ...formData,
                                                discount_percentage: e.target.value,
                                                discount_amount: e.target.value && formData.subtotal
                                                    ? ((formData.subtotal * parseFloat(e.target.value)) / 100).toFixed(2)
                                                    : ""
                                            });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Discount Amount</Label>
                                    <Input
                                        type="number"
                                        value={formData.discount_amount}
                                        onChange={(e) => {
                                            setFormData({
                                                ...formData,
                                                discount_amount: e.target.value,
                                                discount_percentage: e.target.value && formData.subtotal
                                                    ? ((parseFloat(e.target.value) / formData.subtotal) * 100).toFixed(2)
                                                    : ""
                                            });
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Total Amount</Label>
                                    <Input
                                        type="text"
                                        value={formData.total_amount.toFixed(2)}
                                        readOnly
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {formData.invoice_items.length > 0 && (
                            <div className="mt-3">
                                <h6>Added Items</h6>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Description</th>
                                            <th>Quantity</th>
                                            <th>Unit Price</th>
                                            <th>Amount</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formData.invoice_items.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.description}</td>
                                                <td>{item.quantity}</td>
                                                <td>{item.unit_price}</td>
                                                <td>{item.amount}</td>
                                                <td>
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        onClick={() => removeItem(index)}
                                                    >
                                                        <Trash size={14} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Calculation Summary Section */}
                        {/* <div className="mt-4 border-top pt-3">
                            <Row>
                                <Col md={{ size: 4, offset: 8 }}>
                                    <table className="table table-bordered">
                                        <tbody>
                                            <tr>
                                                <td><strong>Subtotal</strong></td>
                                                <td className="text-right">{formData.subtotal.toFixed(2)}</td>
                                            </tr>
                                            {formData.tax_id && (
                                                <tr>
                                                    <td><strong>Tax ({taxes.find(t => t.id == formData.tax_id)?.gst || 0}%)</strong></td>
                                                    <td className="text-right">{formData.tax_amount.toFixed(2)}</td>
                                                </tr>
                                            )}
                                            {formData.discount_percentage && (
                                                <tr>
                                                    <td><strong>Discount ({formData.discount_percentage}%)</strong></td>
                                                    <td className="text-right">-{formData.discount_amount}</td>
                                                </tr>
                                            )}
                                            {formData.discount_amount && !formData.discount_percentage && (
                                                <tr>
                                                    <td><strong>Discount</strong></td>
                                                    <td className="text-right">-{formData.discount_amount}</td>
                                                </tr>
                                            )}
                                            <tr className="font-weight-bold">
                                                <td><strong>Total Amount</strong></td>
                                                <td className="text-right">{formData.total_amount.toFixed(2)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </Col>
                            </Row>
                        </div> */}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => { toggle(); resetForm(); }}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>
                            {formData.id ? "Update" : "Add"}
                            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete invoice {invoiceToDelete?.invoice_number}?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete} disabled={loading}>
                        Delete
                        {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                    </Button>
                </ModalFooter>
            </Modal>

            <Toast ref={toast} />
        </React.Fragment>
    );
};

export default InvoiceList;